import { useMarket } from "@context/marketContext";
import { useSiteInfo } from "@context/siteInfoContext";
import { XIcon } from "@ngg/icons";
import type { CartItem } from "@server/gql/graphql";
import type { Dictionary } from "types/common";

import { cn } from "@/lib/utils";

import Stock from "../Stock/Stock";
import CheckoutItemAction from "./CheckoutItemAction";
import CheckoutItemBadge from "./CheckoutItemBadge";
import CheckoutItemImage from "./CheckoutItemImage";
import CheckoutItemItems from "./CheckoutItemItems";
import CheckoutItemName from "./CheckoutItemName";
import CheckoutItemPrice from "./CheckoutItemPrice";
import CheckoutItemTotal from "./CheckoutItemTotal";
import StockWatchStatus from "./StockWatchStatus";

const CheckoutItem = ({
  dictionary,
  cartItem,
  loading,
  removeItem,
  updateItem,
}: {
  dictionary: Dictionary;
  cartItem: CartItem;
  loading?: boolean;
  removeItem: (item: CartItem) => void;
  updateItem: (item: CartItem) => void;
}) => {
  const { locale } = useMarket().state.market;
  const { dictionary: globalDictionary } = useSiteInfo();

  const [discountCode] =
    cartItem.appliedPromotions?.map((promotion) => promotion.discountCode) ??
    [];
  return (
    <div
      className={cn(
        "relative col-span-full grid grid-cols-[7rem,1fr] grid-rows-[1fr,auto] text-base @2xl:grid-cols-[8rem,1fr]",
      )}
    >
      <CheckoutItemImage
        slug={cartItem.extras?.product_url}
        locale={locale}
        src={cartItem.imageKey}
        alt={cartItem.name}
        className="block h-full min-h-28 w-28 flex-shrink-0 @2xl:row-span-2 @2xl:h-auto @2xl:min-h-32 @2xl:w-32"
      />
      <div className="relative grid grid-flow-row items-center gap-y-1 overflow-hidden px-3 py-2 @2xl:grid-flow-row @2xl:grid-cols-6 @2xl:grid-rows-[1fr,auto]">
        <div className="relative col-span-3 contents w-full flex-col items-start gap-1 place-self-start overflow-hidden @2xl:flex">
          <CheckoutItemName
            className="w-full overflow-hidden"
            locale={locale}
            name={cartItem.name}
            variant={cartItem.extras?.variant}
            slug={cartItem.extras?.product_url}
            brand={cartItem.extras?.brand}
          />
          {cartItem.stockWatchStatus?.ableToComplete && (
            <>
              <Stock
                type="checkout"
                item={cartItem}
                dictionary={globalDictionary}
              />
              <CheckoutItemItems
                items={cartItem.underlyingItems}
                locale={locale}
              />
            </>
          )}
        </div>
        <CheckoutItemAction
          item={cartItem}
          loading={loading}
          update={updateItem}
        />
        <CheckoutItemPrice
          prefix={dictionary.pricePerItem}
          price={cartItem.price}
          priceOriginal={cartItem.priceOriginal}
          locale={locale}
        />
        <CheckoutItemTotal
          prefix={dictionary.total}
          amount={cartItem.totalGrossAmount}
          locale={locale}
        />
        {discountCode && (
          <CheckoutItemBadge type="discount">
            {dictionary.discountCode}: {discountCode}
          </CheckoutItemBadge>
        )}
      </div>
      <StockWatchStatus
        stock={cartItem.stockWatchStatus}
        dictionary={globalDictionary}
        onRemove={() => removeItem(cartItem)}
        onUpdate={(quantity: number) => updateItem({ ...cartItem, quantity })}
        className="col-span-full @2xl:col-span-1 @2xl:m-2"
      />
      {cartItem.stockWatchStatus?.ableToComplete && (
        <button
          type="button"
          className="absolute right-1 top-1 bg-transparent p-2 @2xl:bottom-1 @2xl:top-auto @2xl:p-1"
          onClick={() => removeItem(cartItem)}
        >
          <XIcon className="h-4 w-4 text-grey-300 @2xl:hidden" />
          <span className="hidden text-sm uppercase underline @2xl:block">
            {dictionary?.remove}
          </span>
        </button>
      )}
    </div>
  );
};

CheckoutItem.displayName = "CheckoutItem";

export default CheckoutItem;
